package com.schoolconnect.app.feature.timetable

import com.schoolconnect.app.datasource.network.response.ParentTimetableWeekResponse
import com.schoolconnect.app.datasource.network.response.WeeklyTimetableSlotResponse
import com.schoolconnect.app.datasource.network.response.TimetableViewGridRowResponse
import com.schoolconnect.app.feature.attendance.ParentAttendanceChild
import com.schoolconnect.app.feature.common.ParentChildSelectionStore
import com.schoolconnect.app.repository.timetable.TimetableRepository
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.cancel
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class ParentTimeTableViewModel(initialChildren: List<ParentAttendanceChild> = emptyList()) {
    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)
    private var loadJob: Job? = null
    private val _uiState = MutableStateFlow(ParentTimeTableUiState(children = initialChildren))
    val uiState: StateFlow<ParentTimeTableUiState> = _uiState.asStateFlow()

    init {
        scope.launch {
            ParentChildSelectionStore.selectedChildId.collect { childId ->
                val index = _uiState.value.children.indexOfFirst { it.id == childId }
                if (index >= 0 && index != _uiState.value.selectedChildIndex) onEvent(ParentTimeTableUiEvent.ChildSelected(index))
            }
        }
        loadSelectedChild()
    }

    fun onEvent(event: ParentTimeTableUiEvent) {
        when (event) {
            is ParentTimeTableUiEvent.ChildSelected -> {
                val children = _uiState.value.children
                if (children.isEmpty()) return
                val index = event.index.coerceIn(0, children.lastIndex)
                _uiState.update { it.copy(selectedChildIndex = index, weekly = ParentTimeTableGridUi(), special = ParentTimeTableGridUi(), specialScheduleLabel = null, classLabel = null, weekLabel = null, errorMessage = null) }
                ParentChildSelectionStore.select(children[index].id)
                loadSelectedChild()
            }
            ParentTimeTableUiEvent.Retry -> loadSelectedChild()
        }
    }

    private fun loadSelectedChild() {
        loadJob?.cancel()
        val studentId = _uiState.value.selectedChild?.id?.trim().orEmpty()
        if (studentId.isBlank()) {
            _uiState.update { it.copy(isLoading = false, errorMessage = "No linked student was found.") }
            return
        }
        _uiState.update { it.copy(isLoading = true, errorMessage = null, weekly = ParentTimeTableGridUi(), special = ParentTimeTableGridUi(), specialScheduleLabel = null) }
        loadJob = scope.launch {
            TimetableRepository.loadParentWeek(studentId, refreshScope = scope)
                .onSuccess { response ->
                    val grids = response.toParentGrids()
                    _uiState.update {
                        it.copy(
                            isLoading = false,
                            errorMessage = null,
                            classLabel = listOfNotNull(response.className, response.sectionName).filter(String::isNotBlank).joinToString("-"),
                            weekLabel = weekLabel(response),
                            weekly = grids.weekly,
                            special = grids.special,
                            specialScheduleLabel = grids.specialScheduleLabel
                        )
                    }
                }
                .onFailure { error -> _uiState.update { it.copy(isLoading = false, errorMessage = error.message ?: "Unable to load the timetable. Please try again.") } }
        }
    }

    fun clear() = scope.cancel()
}

private val dayNames = listOf("Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun")
private val fullDayNames = listOf("Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday")

private data class ParentTimetableGrids(
    val weekly: ParentTimeTableGridUi,
    val special: ParentTimeTableGridUi,
    val specialScheduleLabel: String?
)

private fun ParentTimetableWeekResponse.toParentGrids(): ParentTimetableGrids {
    val days = days.orEmpty()
    val flaggedSpecialDays = days.indices.filter { days[it].isSpecialDay }
    val legacySpecialDays = (regularGrid + specialGrid)
        .flatMap { it.cells }
        .filter { it.isSpecialDay }
        .map { it.dayOfWeek }
    val specialDayIndexes = (specialScheduleDays.orEmpty() + flaggedSpecialDays + legacySpecialDays)
        .filter { it in 0..6 }
        .toSet()
    val regularDayIndexes = (0..6).filter { it !in specialDayIndexes }
    val specialScheduleLabel = specialDayIndexes.sorted().joinToString(", ") { fullDayNames[it] }
        .ifBlank { null }

    val usesTeacherShape = days.isNotEmpty() || regularSlots != null || specialSlots != null
    if (usesTeacherShape) {
        return ParentTimetableGrids(
            weekly = teacherShapeGrid(regularSlots.orEmpty(), days, regularDayIndexes, special = false),
            special = teacherShapeGrid(specialSlots.orEmpty(), days, specialDayIndexes.sorted(), special = true),
            specialScheduleLabel = specialScheduleLabel
        )
    }

    return ParentTimetableGrids(
        weekly = legacyGrid(regularGrid, regularDayIndexes),
        special = legacyGrid(specialGrid, specialDayIndexes.sorted()),
        specialScheduleLabel = specialScheduleLabel
    )
}

private fun teacherShapeGrid(
    slots: List<WeeklyTimetableSlotResponse>,
    days: List<com.schoolconnect.app.datasource.network.response.WeeklyTimetableDayResponse>,
    dayIndexes: List<Int>,
    special: Boolean
): ParentTimeTableGridUi {
    val orderedSlots = slots.sortedBy { it.slotOrder }
    if (orderedSlots.isEmpty()) return ParentTimeTableGridUi()
    val rows = dayIndexes.map { dayIndex ->
        val day = days.getOrNull(dayIndex)
        val slotCells = orderedSlots.map { slot ->
            val cells = if (special) slot.specialDayCells ?: slot.regularDayCells
            else slot.regularDayCells ?: slot.specialDayCells
            cells?.getOrNull(dayIndex)
        }
        val isOffDay = day?.isOffDay == true || slotCells.any { it?.isOffDay == true }
        val isHoliday = slotCells.any { it?.isHoliday == true } || day?.cells.orEmpty().any { it.isHoliday }
        val allCellsBlank = slotCells.all { it?.entryText.isNullOrBlank() }
        val holiday = when {
            isOffDay -> slotCells.firstNotNullOfOrNull { it?.entryText?.takeIf(String::isNotBlank) } ?: "School Holiday"
            isHoliday && allCellsBlank -> "School Holiday"
            else -> null
        }
        ParentTimeTableScheduleRow(
            day = day?.dayName?.takeIf(String::isNotBlank)?.take(3) ?: dayNames[dayIndex],
            subjects = slotCells.map { it?.entryText.orEmpty() },
            holiday = holiday
        )
    }
    return ParentTimeTableGridUi(
        times = orderedSlots.map { formatTimeRange(it.fromTime, it.toTime) },
        rows = rows
    )
}

private fun legacyGrid(
    slotRows: List<TimetableViewGridRowResponse>,
    dayIndexes: List<Int>
): ParentTimeTableGridUi {
    val slots = slotRows.sortedBy { it.slotOrder }
    if (slots.isEmpty()) return ParentTimeTableGridUi()
    val rows = dayIndexes.map { dayIndex ->
        val cells = slots.mapNotNull { slot -> slot.cells.firstOrNull { it.dayOfWeek == dayIndex } }
        val holidayCell = cells.firstOrNull { it.isHoliday || it.isOffDay }
        val entry = holidayCell?.entryText?.takeIf(String::isNotBlank)
        ParentTimeTableScheduleRow(
            day = cells.firstOrNull()?.dayName?.takeIf(String::isNotBlank)?.take(3) ?: dayNames[dayIndex],
            subjects = slots.map { slot -> slot.cells.firstOrNull { it.dayOfWeek == dayIndex }?.entryText.orEmpty() },
            holiday = if (holidayCell != null && (entry.equals("School Holiday", true) || cells.all { it.entryText.isNullOrBlank() })) entry ?: "School Holiday" else null
        )
    }
    return ParentTimeTableGridUi(times = slots.map { it.timeRange.toDisplayTime() }, rows = rows)
}

private fun formatTimeRange(from: String?, to: String?): String = "${formatTime(from)} – ${formatTime(to)}"

private fun formatTime(value: String?): String {
    val time = value.orEmpty().split(':')
    val hour = time.getOrNull(0)?.toIntOrNull() ?: return value.orEmpty()
    val minute = time.getOrNull(1)?.take(2) ?: return value.orEmpty()
    val displayHour = (hour % 12).let { if (it == 0) 12 else it }
    return "${displayHour.toString().padStart(2, '0')}:$minute ${if (hour < 12) "AM" else "PM"}"
}

private fun String.toDisplayTime(): String {
    val pieces = split('-', limit = 2).map(String::trim)
    if (pieces.size != 2) return this
    fun format(value: String): String {
        val time = value.split(':')
        val hour = time.getOrNull(0)?.toIntOrNull() ?: return value
        val minute = time.getOrNull(1)?.take(2) ?: return value
        val displayHour = (hour % 12).let { if (it == 0) 12 else it }
        return "${displayHour.toString().padStart(2, '0')}:$minute ${if (hour < 12) "AM" else "PM"}"
    }
    return "${format(pieces[0])} – ${format(pieces[1])}"
}

private fun weekLabel(response: ParentTimetableWeekResponse): String? {
    val start = response.weekStart?.toDisplayDate()
    val end = response.weekEnd?.toDisplayDate()
    return when {
        !start.isNullOrBlank() && !end.isNullOrBlank() -> "$start – $end"
        !start.isNullOrBlank() -> start
        else -> null
    }
}

private fun String.toDisplayDate(): String {
    val parts = split('-')
    if (parts.size != 3) return this
    val month = listOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec").getOrNull(parts[1].toIntOrNull()?.minus(1) ?: -1) ?: return this
    return "${parts[2]} $month ${parts[0]}"
}
